import React, { useMemo } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Legend,
} from "recharts";

/**
 * TimelineChart — plots people (ages projected forward) and life goals on a
 * shared horizontal age-year axis. Uses recharts ScatterChart so we can render
 * multiple parallel "swimlanes" without stacking bars.
 */
export default function TimelineChart({ client }) {
  const data = useMemo(() => buildRows(client), [client]);

  if (!data.rows.length) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        Add family DOBs and life goals to see the life-cycle chart.
      </div>
    );
  }

  const yTicks = data.rows.map((r) => r.name);
  const yLookup = Object.fromEntries(yTicks.map((n, i) => [n, i]));

  const people = data.rows.filter((r) => r.kind === "person")
    .map((r) => ({ x: r.startYear, y: yLookup[r.name], name: r.name, label: `Now (${r.age}yr)` }));
  const milestones = data.rows.flatMap((r) => r.milestones.map((m) => ({
    x: m.year, y: yLookup[r.name], name: r.name, label: m.label, kind: m.kind,
  })));

  return (
    <div className="w-full" data-testid="timeline-chart">
      <ResponsiveContainer width="100%" height={Math.max(240, data.rows.length * 44 + 80)}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 130 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E5E2" />
          <XAxis
            type="number"
            dataKey="x"
            domain={[data.minYear, data.maxYear]}
            ticks={yearTicks(data.minYear, data.maxYear)}
            tick={{ fontSize: 11, fill: "#4A5550" }}
            label={{ value: "Calendar year", position: "insideBottom", offset: -8, fontSize: 11, fill: "#4A5550" }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[-0.5, yTicks.length - 0.5]}
            ticks={yTicks.map((_, i) => i)}
            tickFormatter={(v) => yTicks[v] || ""}
            tick={{ fontSize: 11, fill: "#1A211E" }}
            width={130}
          />
          <ZAxis type="number" range={[80, 80]} />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              return (
                <div className="rounded-md border border-border bg-white px-3 py-2 text-xs shadow">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-muted-foreground">{p.label}</div>
                  <div className="mt-1 text-muted-foreground">Year · {p.x}</div>
                </div>
              );
            }}
          />
          <ReferenceLine x={new Date().getFullYear()} stroke="#0A3A2A" strokeDasharray="4 4" label={{ value: "Today", fontSize: 10, fill: "#0A3A2A" }} />
          <Legend verticalAlign="top" height={28} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          <Scatter name="Family (today)" data={people} fill="#0A3A2A" shape="circle" />
          <Scatter name="Milestones · school / higher ed / retirement / goals" data={milestones} fill="#C48B47" shape="diamond" />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

function yearOf(dateLike) {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  return isNaN(d) ? null : d.getFullYear();
}
function ageNow(dob) {
  const b = yearOf(dob);
  return b == null ? null : new Date().getFullYear() - b;
}

function buildRows(client) {
  const currentYear = new Date().getFullYear();
  const rows = [];

  const pushPerson = (name, dob, milestones = []) => {
    const by = yearOf(dob);
    if (by == null) return;
    rows.push({
      kind: "person", name, dob,
      startYear: currentYear,
      age: currentYear - by,
      milestones,
    });
  };

  if (client.dob) pushPerson("You", client.dob, retirementMilestones(client));
  if (client.spouse?.dob) pushPerson(`Spouse · ${client.spouse.name || ""}`.trim(), client.spouse.dob);

  (client.children || []).forEach((c) => {
    const by = yearOf(c.dob);
    if (by == null) return;
    const ms = [];
    if (c.schooling_start_age) ms.push({ year: by + Number(c.schooling_start_age), label: `Schooling starts (age ${c.schooling_start_age})`, kind: "school" });
    if (c.higher_education_age) ms.push({ year: by + Number(c.higher_education_age), label: `Higher ed (age ${c.higher_education_age})`, kind: "higher-ed" });
    pushPerson(`Child · ${c.name || "child"}`, c.dob, ms);
  });

  (client.dependent_parents || []).forEach((p) => {
    if (!p.dob) return;
    pushPerson(`Parent · ${p.name || ""}`.trim(), p.dob);
  });

  // Goals get their own row so they don't crowd family swimlanes
  const goals = (client.goals || []).map((g) => ({
    year: yearOf(g.target_date), label: `${g.title} · ${g.priority || "Med"}`, kind: "goal",
  })).filter((m) => m.year != null);
  if (goals.length) {
    rows.push({
      kind: "goals", name: "Life goals",
      startYear: currentYear, age: null, dob: null,
      milestones: goals,
    });
  }

  const allYears = rows.flatMap((r) => [r.startYear, ...r.milestones.map((m) => m.year)]).filter(Boolean);
  const minYear = allYears.length ? Math.min(...allYears, currentYear) - 1 : currentYear;
  const maxYear = allYears.length ? Math.max(...allYears, currentYear + 5) + 1 : currentYear + 5;

  return { rows, minYear, maxYear };
}

function retirementMilestones(client) {
  const out = [];
  const sy = yearOf(client.scheduled_retirement_date);
  if (sy) out.push({ year: sy, label: "Scheduled retirement", kind: "retire" });
  const dy = yearOf(client.desired_retirement_date);
  if (dy) out.push({ year: dy, label: "Desired retirement", kind: "retire" });
  return out;
}

function yearTicks(min, max) {
  const span = max - min;
  const step = span > 40 ? 10 : span > 20 ? 5 : span > 10 ? 2 : 1;
  const out = [];
  for (let y = Math.ceil(min / step) * step; y <= max; y += step) out.push(y);
  return out;
}
